import {useState} from 'react';
import {AlertCircle} from 'lucide-react';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldGroup,
  FieldLabel,
  Spinner,
  Switch
} from '@kroll-resolver-crisp/ui-components-react';
import {TargetPolicyViolationsField} from './InvestigationFormFields';
import {type Job} from '@/api/jobs';
import {useClearJobSchedule, useSetJobSchedule} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {ScheduleInput} from '@/components/shared/ScheduleInput';

const DEFAULT_CRON = '0 9 * * *';

type Props = {
  /** The investigation to schedule. The Scheduler re-runs it in place on the cron, like Re-run. */
  readonly job: Job;
  readonly onClose: () => void;
  /** The job as the API returned it, with its schedule set or cleared. */
  readonly onSaved: (job: Job) => void;
};

export function ScheduleDialog({job, onClose, onSaved}: Props) {
  const setSchedule = useSetJobSchedule();
  const clearSchedule = useClearJobSchedule();
  const hasSchedule = job.schedule_cron !== null;

  const [cron, setCron] = useState(job.schedule_cron ?? DEFAULT_CRON);
  const [active, setActive] = useState(hasSchedule ? job.schedule_active : true);
  const [policyViolations, setPolicyViolations] = useState([...job.target_policy_violations]);
  const [error, setError] = useState<string | null>(null);

  const submitting = setSchedule.isPending || clearSchedule.isPending;
  const canSubmit = Boolean(cron.trim()) && policyViolations.length > 0;

  const run = async (action: () => Promise<Job>, failure: string) => {
    setError(null);
    try {
      onSaved(await action());
    } catch (error_: unknown) {
      setError(getApiErrorMessage(error_, failure));
    }
  };

  const save = async () =>
    run(
      async () =>
        setSchedule.mutateAsync({
          id: job.id,
          body: {
            cron_expression: cron,
            is_active: active,
            target_policy_violations: policyViolations
          }
        }),
      'Failed to save schedule'
    );

  const remove = async () =>
    run(async () => clearSchedule.mutateAsync(job.id), 'Failed to remove schedule');

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule investigation</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <FieldGroup className="gap-4">
            {/* The API refuses a schedule that runs more than once a day. */}
            <ScheduleInput
              allowInterval={false}
              value={cron}
              onChange={(c) => {
                setCron(c ?? DEFAULT_CRON);
              }}
            />

            <Field orientation="horizontal">
              <Switch id="schedule-active" checked={active} onCheckedChange={setActive} />
              <FieldLabel htmlFor="schedule-active">{active ? 'Active' : 'Paused'}</FieldLabel>
            </Field>

            <TargetPolicyViolationsField value={policyViolations} onChange={setPolicyViolations} />

            {error ? (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            ) : null}
          </FieldGroup>
        </DialogBody>

        <DialogFooter sticky>
          {hasSchedule ? (
            <Button
              variant="destructive"
              size="sm"
              className="mr-auto"
              disabled={submitting}
              onClick={() => void remove()}>
              Remove schedule
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={submitting || !canSubmit} onClick={() => void save()}>
            {submitting ? <Spinner /> : null}
            Save schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
